<template>
  <!-- Booking from the student app (Miguel, 21-oct, answer 5): the student
       picks a type, a day and a free slot of his teachers; with the school's
       direct booking it is booked at once, with booking by request it waits
       for the school. Only shown when the school has it on. -->
  <div class="student-book navigation-top-app">
    <navigation-top-app
      :app="app"
      :menu-screen="true"
      :back="() => {
        $router.push({name: 'app.student.profile', params: {code: app.user.code}, query: {origin: 'app', navigationLeft: 'false', token: app.token}})
      }"
    >
      <template v-slot:center>
        <div id="navigation-center">
          {{ s.title }}
        </div>
      </template>
    </navigation-top-app>

    <div id="student-book-content" class="max-width-1100 margin-auto">
      <div class="book-loading" v-if="booking.loading && !booking.loaded">
        <loader-dot :size="'16px'" :color_1="'rgb(32, 113, 183)'" :color_2="'rgb(32, 113, 183)'" />
      </div>

      <template v-if="booking.loaded && booking.mode !== 'off'">
        <!-- Requests decided by the school since the student last looked. -->
        <div class="book-notice" v-for="r in notices" :key="'n' + r.id" :class="r.status">
          <font-awesome-icon :icon="['fas', r.status === 'approved' ? 'check-circle' : 'times-circle']" />
          <div>
            <b>{{ r.status === 'approved' ? s.notice_approved : s.notice_rejected }}</b>
            <span>{{ s.kind[r.kind] }} · {{ requestWhen(r) }}</span>
            <span class="book-notice-reason" v-if="r.status === 'rejected' && r.decided_reason">«{{ r.decided_reason }}»</span>
          </div>
          <button class="book-action" v-on:click="seen(r)">{{ s.notice_ok }}</button>
        </div>

        <div class="book-layout">
          <section class="book-card book-main" v-if="perm.create !== 'off' || moving">
            <div class="book-moving" v-if="moving">
              <span>{{ s.moving }} {{ moving.date | humanDate(false) }} · {{ moving.start }}</span>
              <button class="book-action" v-on:click="stopMoving()">{{ s.moving_cancel }}</button>
            </div>
            <p class="book-intro" v-else>{{ perm.create === 'direct' ? s.mode_direct : s.mode_request }}</p>

            <template v-if="types.length > 0">
              <div class="book-label">{{ s.type }}</div>
              <div class="book-types">
                <button v-for="t in types" :key="t.id" class="book-type" :class="{selected: selectedType && selectedType.id === t.id}" :disabled="moving !== null" v-on:click="selectType(t)">
                  <b>{{ t.name }}</b>
                  <span>{{ t.duration }} min · {{ t.license }}<template v-if="booking.useBalanceInfo"> · {{ t.balance === 1 ? s.left_one : s.left }} {{ t.balance }} {{ t.balance === 1 ? s.class : s.classes }}</template></span>
                </button>
              </div>

              <div class="book-week">
                <button class="book-week-arrow" :disabled="weekStart <= 0" :title="s.week_prev" v-on:click="shiftWeek(-7)"><font-awesome-icon :icon="['fas', 'chevron-left']" /></button>
                <div class="book-week-label">{{ weekLabel }}</div>
                <div class="book-days">
                  <button v-for="d in slots.days" :key="d.date" class="book-day" :class="{selected: selectedDay === d.date, empty: d.slots.length === 0}" v-on:click="selectedDay = d.date">
                    <span>{{ weekday(d.date) }}</span>
                    <b>{{ dayNumber(d.date) }}</b>
                    <i v-if="d.slots.length > 0">{{ d.slots.length }} <small>{{ d.slots.length === 1 ? s.slot_one : s.slot_many }}</small></i>
                    <i class="full" v-else>{{ s.full }}</i>
                  </button>
                </div>
                <button class="book-week-arrow" :title="s.week_next" v-on:click="shiftWeek(7)"><font-awesome-icon :icon="['fas', 'chevron-right']" /></button>
              </div>

              <div class="book-reason" v-if="!slots.loading && !slots.canBook">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
                <span>{{ slots.reason }}</span>
              </div>
              <div class="book-slots-loading" v-else-if="slots.loading">{{ s.loading }}</div>
              <template v-else>
                <div class="book-slots" v-if="daySlots.length > 0">
                  <button v-for="(slot, i) in daySlots" :key="slot.start + slot.teacher.code + i" class="book-slot" v-on:click="pick(slot)">
                    <b>{{ slot.start }}</b>
                    <span v-if="manyTeachers">{{ firstName(slot.teacher.name) }}</span>
                  </button>
                </div>
                <div class="book-empty" v-else>{{ s.no_slots }}</div>
              </template>
            </template>
            <div class="book-empty" v-else>
              <font-awesome-icon :icon="['far', 'calendar-alt']" />
              <span>{{ s.no_types }}</span>
            </div>
          </section>

          <aside class="book-side">
            <section class="book-card">
              <h3>{{ s.upcoming }}</h3>
              <div class="book-empty small" v-if="booking.upcoming.length === 0">{{ s.upcoming_empty }}</div>
              <div class="book-class" v-for="p in booking.upcoming" :key="p.id">
                <div class="book-class-when">
                  <b>{{ p.date | humanDate(false) }}</b>
                  <span>{{ p.start }}–{{ endOf(p) }} · {{ p.type.name }}</span>
                  <span>{{ s.with }} {{ firstName(p.teacher.name) }} · {{ p.vehicle.name }}</span>
                  <span class="book-pill pending" v-if="pendingFor(p, 'move')">{{ s.pending_change }}</span>
                  <span class="book-pill pending" v-if="pendingFor(p, 'cancel')">{{ s.pending_cancel }}</span>
                </div>
                <div class="book-class-actions" v-if="!pendingFor(p, 'move') && !pendingFor(p, 'cancel')">
                  <button class="book-action" v-if="perm.move !== 'off'" v-on:click="startMoving(p)">{{ perm.move === 'direct' ? s.change : s.change_request }}</button>
                  <button class="book-action danger" v-if="perm.cancel !== 'off' && booking.cancel.allowed" v-on:click="cancel(p)">{{ perm.cancel === 'direct' ? s.cancel : s.cancel_request }}</button>
                </div>
              </div>
            </section>

            <section class="book-card" v-if="booking.requests.length > 0">
              <h3>{{ s.requests }}</h3>
              <div class="book-request" v-for="r in booking.requests.slice(0, 8)" :key="'r' + r.id">
                <div>
                  <b>{{ s.kind[r.kind] }}</b>
                  <span>{{ requestWhen(r) }}</span>
                  <span class="book-notice-reason" v-if="r.status === 'rejected' && r.decided_reason">«{{ r.decided_reason }}»</span>
                </div>
                <span class="book-pill" :class="r.status">{{ s.status[r.status] }}</span>
              </div>
            </section>
          </aside>
        </div>
      </template>
    </div>

    <!-- Confirmation sheet. -->
    <transition name="fade">
      <div class="book-sheet-backdrop" v-if="chosen" v-on:click.self="chosen = null">
        <div class="book-sheet">
          <h3>{{ confirmTitle }}</h3>
          <div class="book-sheet-facts">
            <div><span>{{ s.confirm_when }}</span><b>{{ selectedDay | humanDate(false) }} · {{ chosen.start }}–{{ chosen.end }}</b></div>
            <div><span>{{ s.confirm_type }}</span><b>{{ selectedType.name }} · {{ selectedType.duration }} min</b></div>
            <div><span>{{ s.confirm_teacher }}</span><b>{{ chosen.teacher.name }}</b></div>
            <div><span>{{ s.confirm_vehicle }}</span><b>{{ chosen.vehicle.name }} · {{ chosen.vehicle.code }}</b></div>
          </div>
          <template v-if="modeNow === 'request'">
            <label class="book-label">{{ s.confirm_comment }}</label>
            <textarea class="book-comment" maxlength="255" v-model="comment"></textarea>
          </template>
          <div class="book-sheet-actions">
            <button class="book-action big" :disabled="saving" v-on:click="chosen = null">{{ s.confirm_back }}</button>
            <button class="h-button default" :disabled="saving" v-on:click="save()">{{ confirmButton }}</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const SEEN_KEY = 'hermes-student-requests-seen'

export default {
  name: 'app-student-practice-book-view',
  props: [
    'app'
  ],
  data () {
    return {
      booking: {
        loading: false,
        loaded: false,
        mode: 'off',
        // Each action on its own: off, direct or request.
        permissions: {create: 'off', move: 'off', cancel: 'off'},
        cancel: {allowed: true, refund: true, refund_min_hours: 0},
        useBalanceInfo: true,
        types: [],
        upcoming: [],
        requests: []
      },
      selectedType: null,
      weekStart: 0,
      selectedDay: '',
      slots: {
        loading: false,
        canBook: true,
        reason: '',
        days: []
      },
      moving: null,
      chosen: null,
      comment: '',
      saving: false,
      seenIds: []
    }
  },
  computed: {
    s () {
      return this.app.strings.app.student.practice.book
    },
    perm () {
      return this.booking.permissions
    },
    // Mode of what the student is doing now: moving a class or booking one.
    modeNow () {
      return this.moving ? this.perm.move : this.perm.create
    },
    types () {
      return this.booking.types
    },
    daySlots () {
      let day = this.slots.days.find((d) => d.date === this.selectedDay)
      return day ? day.slots : []
    },
    manyTeachers () {
      let codes = {}
      for (let d of this.slots.days) {
        for (let slot of d.slots) {
          codes[slot.teacher.code] = true
        }
      }
      return Object.keys(codes).length > 1
    },
    // "3 – 9 nov", or "28 nov – 4 dic" across two months.
    weekLabel () {
      let days = this.slots.days
      if (days.length === 0) {
        return ''
      }
      let months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
      let first = new Date(days[0].date + 'T00:00:00')
      let last = new Date(days[days.length - 1].date + 'T00:00:00')
      let start = first.getDate() + (first.getMonth() === last.getMonth() ? '' : ' ' + months[first.getMonth()])
      return start + ' – ' + last.getDate() + ' ' + months[last.getMonth()]
    },
    notices () {
      return this.booking.requests.filter((r) => (r.status === 'approved' || r.status === 'rejected') && this.seenIds.indexOf(r.id) < 0)
    },
    confirmTitle () {
      if (this.moving) {
        return this.modeNow === 'direct' ? this.s.confirm_title_move : this.s.confirm_title_move_request
      }
      return this.modeNow === 'direct' ? this.s.confirm_title_book : this.s.confirm_title_request
    },
    confirmButton () {
      if (this.moving) {
        return this.modeNow === 'direct' ? this.s.confirm_move : this.s.confirm_move_request
      }
      return this.modeNow === 'direct' ? this.s.confirm_book : this.s.confirm_request
    }
  },
  mounted () {
    try {
      this.seenIds = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')
    } catch (e) {
      this.seenIds = []
    }
    this.load()
  },
  methods: {
    headers () {
      return {Authorization: 'Token ' + this.app.token}
    },
    load (keepSelection) {
      if (this.booking.loading) {
        return
      }
      this.booking.loading = true
      this.$http.get(this.app.url.api + this.app.endpoints.practice.booking, {headers: this.headers()}).then(response => {
        this.booking.loading = false
        if (response.status !== 200 || response.body.success !== true) {
          this.failed(response)
          return
        }
        let data = response.body.data
        this.booking.mode = data.booking
        this.booking.loaded = true
        if (data.booking === 'off') {
          // The school does not allow it: nothing of this is shown.
          this.$router.replace({name: 'app.student.profile', params: {code: this.app.user.code}, query: {origin: 'app', navigationLeft: 'false', token: this.app.token}}).catch(() => {})
          return
        }
        this.booking.permissions = data.permissions
        this.booking.cancel = data.cancel
        this.booking.types = data.types
        this.booking.upcoming = data.upcoming.map((p) => Object.assign(p, {date: new Date(p.date + 'T00:00:00')}))
        this.booking.requests = data.requests
        if (this.perm.create === 'off' && !this.moving) {
          // Booking off: only his classes, to move or cancel them.
        } else if (!keepSelection || !this.selectedType) {
          let first = this.types.find((t) => t.balance > 0) || this.types[0]
          if (first) {
            this.selectType(first)
          }
        } else {
          this.loadSlots()
        }
      }, () => {
        this.booking.loading = false
        this.showError(this.app.strings, 'request', 'status-network: Connection unreachable')
      })
    },
    failed (response) {
      if (response.body && response.body.error && response.body.error.code === 'S100') {
        this.app.methods.logout()
        return
      }
      if (response.body && response.body.error) {
        this.showRequestError(this.app.strings, 'student-practice-book', response.body.error)
      } else {
        this.showError(this.app.strings, 'request', 'status-' + response.status)
      }
    },
    selectType (t) {
      this.selectedType = t
      this.weekStart = 0
      this.loadSlots()
    },
    shiftWeek (days) {
      this.weekStart = Math.max(0, this.weekStart + days)
      this.loadSlots()
    },
    loadSlots () {
      if (!this.selectedType) {
        return
      }
      let from = new Date()
      from.setDate(from.getDate() + this.weekStart)
      let query = '?type_id=' + this.selectedType.id + '&days=7&from=' + this.isoDate(from)
      if (this.moving) {
        query += '&exclude=' + this.moving.id
      }
      this.slots.loading = true
      this.$http.get(this.app.url.api + this.app.endpoints.practice.slots + query, {headers: this.headers()}).then(response => {
        this.slots.loading = false
        if (response.status !== 200 || response.body.success !== true) {
          this.failed(response)
          return
        }
        let data = response.body.data
        this.slots.canBook = data.can_book
        this.slots.reason = data.reason
        this.slots.days = data.days.length > 0 ? data.days : this.emptyWeek(from)
        let withSlots = this.slots.days.find((d) => d.slots.length > 0)
        if (!this.slots.days.find((d) => d.date === this.selectedDay)) {
          this.selectedDay = (withSlots || this.slots.days[0]).date
        }
      }, () => {
        this.slots.loading = false
        this.showError(this.app.strings, 'request', 'status-network: Connection unreachable')
      })
    },
    emptyWeek (from) {
      let days = []
      for (let i = 0; i < 7; i++) {
        let d = new Date(from)
        d.setDate(d.getDate() + i)
        days.push({date: this.isoDate(d), slots: []})
      }
      return days
    },
    isoDate (d) {
      let pad = (n) => (n < 10 ? '0' : '') + n
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
    },
    weekday (date) {
      return ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][new Date(date + 'T00:00:00').getDay()]
    },
    dayNumber (date) {
      return new Date(date + 'T00:00:00').getDate()
    },
    firstName (name) {
      return (name || '').split(' ')[0]
    },
    endOf (p) {
      let parts = p.start.split(':')
      let minutes = parseInt(parts[0]) * 60 + parseInt(parts[1]) + p.duration
      let pad = (n) => (n < 10 ? '0' : '') + n
      return Math.floor(minutes / 60) + ':' + pad(minutes % 60)
    },
    requestWhen (r) {
      let p = r.practice_id ? this.booking.upcoming.find((u) => u.id === r.practice_id) : null
      let date = r.data.date || (p ? this.isoDate(p.date) : '')
      let start = r.data.start || (p ? p.start : '')
      if (!date) {
        return ''
      }
      let when = this.$options.filters.humanDate(new Date(date + 'T00:00:00'), false) + (start ? ' · ' + start : '')
      // A change says from which class it comes.
      if (r.kind === 'move' && p && r.data.date) {
        return this.$options.filters.humanDate(p.date, false) + ' · ' + p.start + ' → ' + when
      }
      return when
    },
    pendingFor (p, kind) {
      return this.booking.requests.some((r) => r.practice_id === p.id && r.kind === kind && (r.status === 'pending' || r.status === 'approving'))
    },
    seen (r) {
      this.seenIds.push(r.id)
      try {
        localStorage.setItem(SEEN_KEY, JSON.stringify(this.seenIds.slice(-200)))
      } catch (e) {
        // Private mode: the notice comes back next time.
      }
    },
    startMoving (p) {
      let t = this.types.find((x) => x.id === p.type.id)
      this.moving = p
      this.selectedType = t || {id: p.type.id, name: p.type.name, duration: p.duration, license: p.license, price: p.price, circuit: p.circuit, exam: p.exam}
      this.weekStart = 0
      this.loadSlots()
      window.scrollTo({top: 0, behavior: 'smooth'})
    },
    stopMoving () {
      this.moving = null
      this.loadSlots()
    },
    pick (slot) {
      this.chosen = slot
      this.comment = ''
    },
    save () {
      let slot = this.chosen
      let t = this.selectedType
      let parts = slot.start.split(':')
      let base = this.moving
      let practice = {
        id: base ? base.id : 0,
        license: base ? base.license : t.license,
        circuit: base ? base.circuit : t.circuit,
        exam: base ? base.exam : t.exam,
        student: this.app.user.code,
        teacher: slot.teacher.code,
        vehicle: slot.vehicle.code,
        date: this.selectedDay,
        hour: parseInt(parts[0]),
        minute: parseInt(parts[1]),
        time: t.duration,
        price: base ? base.price : t.price,
        color: base ? base.color : '',
        type_id: t.id,
        observations: base ? base.observations : '',
        meeting_point: base ? base.meeting_point : 'school',
        meeting_address: base ? base.meeting_address : ''
      }
      this.saving = true
      this.$http.post(
        this.app.url.api + this.app.endpoints.practice.create,
        {practice: JSON.stringify(practice), past: 'false', comment: this.comment.trim()},
        {emulateJSON: true, headers: this.headers()}
      ).then(response => {
        this.saving = false
        if (response.status === 200 && response.body.success === true) {
          let request = response.body.data && response.body.data.request
          let text = request ? this.s.done_request : (this.moving ? this.s.done_move : this.s.done_book)
          this.chosen = null
          this.moving = null
          this.$swal(request ? {title: text, icon: 'success'} : {title: text, icon: 'success', timer: 1800, showConfirmButton: false})
          this.load(true)
          return
        }
        this.saveFailed(response)
      }, () => {
        this.saving = false
        this.showError(this.app.strings, 'request', 'status-network: Connection unreachable')
      })
    },
    saveFailed (response) {
      let error = response.body && response.body.error
      if (error && error.code === 'P102') {
        this.chosen = null
        this.$swal({title: this.s.errors.busy, icon: 'warning'})
        this.loadSlots()
      } else if (error && error.code === 'P103') {
        this.$swal({title: this.s.errors.balance, icon: 'warning'})
      } else if (error) {
        this.showRequestError(this.app.strings, 'student-practice-book-save', error)
      } else {
        this.showError(this.app.strings, 'request', 'status-' + response.status)
      }
    },
    cancel (p) {
      let direct = this.perm.cancel === 'direct'
      let text = direct ? (this.booking.cancel.refund ? this.s.cancel_text_refund : this.s.cancel_text_no_refund) : this.s.cancel_text_request
      this.$swal({
        title: this.s.cancel_title,
        text: this.$options.filters.humanDate(p.date, false) + ' · ' + p.start + '. ' + text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: direct ? this.s.cancel_confirm : this.s.cancel_confirm_request,
        cancelButtonText: this.s.cancel_keep,
        customClass: {popup: 'swal-danger'}
      }).then((result) => {
        if (result.value) {
          this.sendCancel(p, this.booking.cancel.refund)
        }
      })
    },
    sendCancel (p, refund) {
      this.$http.post(
        this.app.url.api + this.app.endpoints.practice.cancel,
        {ids: JSON.stringify([p.id]), balance: refund ? 'true' : 'false', observations: ''},
        {emulateJSON: true, headers: this.headers()}
      ).then(response => {
        if (response.status === 200 && response.body.success === true) {
          let request = response.body.data && response.body.data.requests
          this.$swal({title: request ? this.s.done_cancel_request : this.s.done_cancel, icon: 'success', timer: 1800, showConfirmButton: false})
          this.load(true)
          return
        }
        let error = response.body && response.body.error
        if (error && error.code === 'P107' && refund) {
          // Too late to give it back: offer to cancel without refund.
          this.$swal({title: this.s.late_title, text: error.detail, icon: 'warning', showCancelButton: true, confirmButtonText: this.s.late_confirm, cancelButtonText: this.s.cancel_keep, customClass: {popup: 'swal-danger'}}).then((result) => {
            if (result.value) {
              this.sendCancel(p, false)
            }
          })
          return
        }
        this.saveFailed(response)
      }, () => {
        this.showError(this.app.strings, 'request', 'status-network: Connection unreachable')
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/variables-app.scss";
@import "@/assets/styles/variables-valkiria.scss";

#student-book-content {
  position: relative;
  width: 100%;
  min-height: 100%;
  padding: 72px 0 32px;
  box-sizing: border-box;
  color: $text-color-1;
}

.max-width-1100 {
  max-width: 1100px;
}

.book-loading {
  padding-top: 40px;
}

.book-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.book-card {
  background-color: #FFFFFF;
  border: 1px solid $color-grey-light;
  border-radius: 20px;
  box-shadow: 0 12px 30px rgba(20, 67, 107, 0.06);
  padding: 18px 16px;
  box-sizing: border-box;
}

.book-card h3 {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 700;
}

.book-side {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.book-intro {
  margin: 0 0 14px;
  color: $text-color-2;
  font-size: 14px;
  line-height: 1.45;
}

.book-label {
  display: block;
  margin: 4px 0 8px;
  color: $text-color-2;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.book-types {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.book-type {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 10px 14px;
  border: 1px solid $color-grey-light;
  border-radius: 14px;
  background-color: $color-grey-light-3;
  color: $text-color-1;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.book-type b {
  font-size: 14px;
}

.book-type span {
  color: $text-color-2;
  font-size: 12px;
}

.book-type.selected {
  border-color: $color-primary-1;
  background-color: #eaf3fb;
  box-shadow: inset 0 0 0 1px $color-primary-1;
}

.book-type:disabled {
  cursor: default;
}

.book-week {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
}

.book-week-arrow {
  flex: 0 0 40px;
  height: 40px;
  border: 1px solid $color-grey-light;
  border-radius: 999px;
  background-color: #FFFFFF;
  color: $color-primary-1;
  cursor: pointer;
}

.book-week-label {
  display: none;
  color: $text-color-1;
  font-size: 15px;
  font-weight: 700;
}

.book-week-arrow:disabled {
  color: $text-color-3;
  cursor: default;
}

.book-days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 4px;
  flex: 1;
}

.book-day {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 0;
  border: 1px solid transparent;
  border-radius: 12px;
  background-color: transparent;
  color: $text-color-1;
  font: inherit;
  cursor: pointer;
}

.book-day span {
  color: $text-color-2;
  font-size: 11px;
  text-transform: uppercase;
}

.book-day b {
  font-size: 17px;
}

.book-day i {
  margin-top: 2px;
  font-style: normal;
  font-size: 10px;
  line-height: 1.15;
  color: $color-success;
  font-weight: 700;
  text-align: center;
}

.book-day i.full {
  color: $text-color-3;
  font-weight: 600;
}

.book-day.empty b {
  color: $text-color-3;
}

.book-day.selected {
  border-color: $color-primary-1;
  background-color: #eaf3fb;
}

.book-slots {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 8px;
}

.book-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 4px;
  border: 1px solid #cfe2f3;
  border-radius: 12px;
  background-color: #FFFFFF;
  color: $color-primary-1;
  font: inherit;
  cursor: pointer;
}

.book-slot b {
  font-size: 15px;
}

.book-slot span {
  color: $text-color-2;
  font-size: 11px;
}

.book-slot:hover {
  background-color: #eaf3fb;
}

.book-empty,
.book-slots-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 8px;
  color: $text-color-2;
  font-size: 14px;
  text-align: center;
}

.book-empty > svg {
  color: $text-color-3;
  font-size: 26px;
}

.book-empty.small {
  padding: 8px 0;
  align-items: flex-start;
  text-align: left;
}

.book-reason {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 14px;
  background-color: #fff4e6;
  color: #9a5b0a;
  font-size: 14px;
}

.book-moving {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 14px;
  background-color: #eaf3fb;
  color: $color-primary-2;
  font-size: 14px;
  font-weight: 600;
}

.book-moving .book-action {
  flex: 0 0 auto;
}

.book-class {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 0;
  border-top: 1px solid $color-grey-light;
}

.book-class:first-of-type {
  border-top: none;
  padding-top: 0;
}

.book-class-when {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 14px;
}

.book-class-when span {
  color: $text-color-2;
  font-size: 13px;
}

.book-class-actions {
  display: flex;
  gap: 8px;
}

.book-action {
  height: 40px;
  padding: 0 16px;
  border: 1px solid $color-grey-1;
  border-radius: 999px;
  background-color: #FFFFFF;
  color: $text-color-1;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.book-action.danger {
  border-color: #f3c0c5;
  color: $color-danger;
}

.book-action.big {
  height: 44px;
  font-size: 15px;
}

.book-action:disabled {
  opacity: 0.6;
  cursor: default;
}

.book-pill {
  align-self: flex-start;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background-color: $color-grey-light-2;
  color: $text-color-2;
}

.book-pill.pending,
.book-pill.approving {
  background-color: #fff4e6;
  color: #b45309;
}

.book-pill.approved {
  background-color: rgba(22, 163, 74, 0.12);
  color: #15803d;
}

.book-pill.rejected {
  background-color: rgba(220, 38, 38, 0.1);
  color: #b91c1c;
}

.book-request {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-top: 1px solid $color-grey-light;
  font-size: 14px;
}

.book-request:first-of-type {
  border-top: none;
  padding-top: 0;
}

.book-request > .book-pill {
  flex: 0 0 auto;
  white-space: nowrap;
}

.book-request > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.book-request span {
  color: $text-color-2;
  font-size: 13px;
}

.book-notice {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 16px;
  font-size: 14px;
}

.book-notice > svg {
  font-size: 22px;
  flex: 0 0 auto;
}

.book-notice > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.book-notice.approved {
  background-color: #e9f8ef;
  color: #166534;
}

.book-notice.rejected {
  background-color: #fdecee;
  color: #991b1b;
}

.book-notice-reason {
  font-style: italic;
}

.book-sheet-backdrop {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background-color: rgba(15, 23, 42, 0.45);
}

.book-sheet {
  width: 100%;
  max-width: 480px;
  padding: 22px 18px 26px;
  border-radius: 22px 22px 0 0;
  background-color: #FFFFFF;
  box-sizing: border-box;
}

.book-sheet h3 {
  margin: 0 0 14px;
  font-size: 18px;
}

.book-sheet-facts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 14px;
}

.book-sheet-facts > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 12px;
  background-color: $color-grey-light-3;
}

.book-sheet-facts span {
  color: $text-color-2;
  font-size: 12px;
}

.book-sheet-facts b {
  font-size: 14px;
}

.book-comment {
  width: 100%;
  min-height: 64px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid $color-grey-light;
  border-radius: 12px;
  font: inherit;
  box-sizing: border-box;
}

.book-sheet-actions {
  display: flex;
  gap: 10px;
}

.book-sheet-actions .h-button,
.book-sheet-actions .book-action {
  flex: 1;
}

@media (max-width: 480px) {
  .book-card {
    padding: 16px 12px;
  }

  .book-week {
    gap: 10px 2px;
  }

  /* The arrows go on their own row so the seven days fit the whole width:
     no sideways scrolling in the app. */
  .book-week {
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .book-week-label {
    display: block;
  }

  .book-days {
    order: 3;
    flex: 0 0 100%;
  }

  .book-day {
    padding: 6px 0;
  }

  /* The notice's button goes under its text: beside it, the text piles up. */
  .book-notice {
    flex-wrap: wrap;
  }

  .book-notice > .book-action {
    flex: 1 0 100%;
  }

  /* A seventh of a phone only holds the number; a full day is already greyed. */
  .book-day i small {
    display: none;
  }

  .book-day i.full {
    visibility: hidden;
  }

  .book-day span {
    font-size: 10px;
  }

  .book-day b {
    font-size: 16px;
  }
}

@media (min-width: 1024px) {
  #student-book-content {
    padding-top: 92px;
  }

  .book-layout {
    flex-direction: row;
    align-items: flex-start;
  }

  .book-main {
    flex: 1 1 auto;
    padding: 24px;
  }

  .book-side {
    flex: 0 0 340px;
  }

  /* Booking off: only his classes, across the whole width. */
  .book-side:only-child {
    flex: 1 1 auto;
  }

  .book-sheet-backdrop {
    align-items: center;
  }

  .book-sheet {
    border-radius: 22px;
  }
}
</style>
